import React, { useEffect, useState, useRef } from 'react'
import { Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CenterFocusStrong, Home } from '@material-ui/icons'
import { useMapEngine } from '../shared'
import { MAP_ID, getCountries, getBaseLayers } from '../../utils/apiHelper'
import { getHomePosition, LAYER_KIND } from '../../utils/configQueries'
import { FocusControl } from '../FocusControl'
import { BaseLayersControl } from '../BaseLayersControl'
import { Layer } from '../Layer'
import { useGlobalContext, getLanguage } from '../../state'

import { useLayer } from '@facility-latam/geoportal-components'

export const Map = () => {
  const {
    state: { layers }
  } = useGlobalContext()
  const containerRef = useRef()
  const [countries, setCountries] = useState()
  const [engine] = useMapEngine(containerRef)
  const [baseLayers, setBaseLayers] = useState([])
  const [baseLayerId, setBaseLayerId] = useState()

  useLayer({
    engine,
    source: {
      id: 'pdi_trifinio',
      type: 'raster',
      tiles: [
        // eslint-disable-next-line max-len
        'https://geoportal.cepal.org/geoserver/ows?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.3.0&request=GetMap&srs=EPSG:3857&transparent=true&width=256&height=256&layers=geonode:pdi_trifinio'
      ],
      tileSize: 256
    },
    layer: {
      id: 'pdi_trifinio',
      type: 'raster',
      source: 'pdi_trifinio',
      layout: {
        visibility: 'none'
      },
      paint: {}
    }
  })

  const [setLayerData] = useLayer({
    engine,
    layer: {
      id: 'frontiers',
      type: 'line',
      source: 'countries',
      layout: {
        visibility: 'visible'
      },
      paint: {
        'line-color': 'white',
        'line-width': 0.8
      }
    }
  })
  const [setCountriesLabelsData] = useLayer({
    engine,
    layer: {
      id: 'countriesLabels',
      type: 'symbol',
      source: 'countriesLabels',
      layout: {
        visibility: 'none',
        'text-field': ['get', getLanguage() === 'en' ? 'name_en' : 'name_es'],
        'text-font': ['Open Sans Regular'],
        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
        'text-radial-offset': 0.5,
        'text-justify': 'auto'
      }
    }
  })

  const [setLabelsData] = useLayer({
    engine,
    layer: {
      id: 'labelsData',
      type: 'symbol',
      source: 'labelsData',
      minzoom: 5,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'name_es'],
        'text-font': ['Open Sans Regular'],
        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
        'text-radial-offset': 0.5,
        'text-justify': 'auto'
      }
    }
  })

  useEffect(() => {
    getCountries().then((geodata) => {
      setCountries(geodata.countries)
    })
    getBaseLayers().then(({ layers }) => {
      setBaseLayers(layers)
    })
  }, [])

  const setFeaturesLabels = (features) => {
    const setFeatures = features?.map(
      ({
        type,
        properties: { long, lat, name_en, name_es },
        geometry: { type: _type, coordinates: _coordinates }
      }) => ({
        type,
        properties: { name_es, name_en },
        geometry: {
          type: _type === 'Polygon' ? _type : 'Point',
          coordinates: long ? [long, lat] : _coordinates
        }
      })
    )
    return setFeatures
  }

  useEffect(() => {
    const visibleLayer = layers.filter(({ isVisible }) => isVisible)[0]
    if (engine && setLabelsData && visibleLayer) {
      const visibleSoure = engine.getSource(visibleLayer.id)?._data
      const labelsData = {
        type: 'FeatureCollection',
        features: setFeaturesLabels(visibleSoure?.features)
      }

      visibleLayer.isCountry
        ? setCountriesLabelsData(labelsData)
        : setLabelsData(labelsData)
    }
  }, [engine, layers, setLabelsData, setCountriesLabelsData])

  useEffect(() => {
    if (engine && countries && setLayerData) {
      setLayerData(countries)
    }
  }, [countries, engine, setLayerData])

  useEffect(() => {
    if (engine) {
      layers
        .filter(({ isVisible }) => isVisible)
        .forEach((layer) => {
          const { id: idLayers, opacity, kind, layerType } = layer

          // set opacity property
          if ([LAYER_KIND.EXTERNAL, LAYER_KIND.INDICATOR].includes(kind)) {
            const opacityProperty =
              kind === LAYER_KIND.EXTERNAL
                ? ['raster-opacity', opacity / 100]
                : [
                    `${
                      layerType === 'fill' ? 'fill-opacity' : 'circle-opacity'
                    }`,
                    [
                      'case',
                      ['boolean', ['feature-state', 'hover'], false],
                      (opacity / 100) * 0.7,
                      opacity / 100
                    ]
                  ]
            engine.setPaintProperty(idLayers, ...opacityProperty)
          }

          // when the pointer hover the layer the opacity property changes
          let hoveredStateId = null
          engine.on('mousemove', idLayers, ({ point }) => {
            const features = engine.queryRenderedFeatures(point)
            if (features[0].id | features[1]?.id) {
              if (hoveredStateId !== null) {
                engine.setFeatureState(
                  { source: idLayers, id: hoveredStateId },
                  { hover: false }
                )
              }
              hoveredStateId = features[0].id ? features[0].id : features[1].id
              engine.setFeatureState(
                { source: idLayers, id: hoveredStateId },
                { hover: true }
              )
            }
          })
          engine.on('mouseleave', idLayers, () => {
            if (hoveredStateId !== null) {
              engine.setFeatureState(
                { source: idLayers, id: hoveredStateId },
                { hover: false }
              )
            }
            hoveredStateId = null
          })
        })
    }
  }, [engine, layers])

  const useStyles = makeStyles((theme) => ({
    root: {
      width: `calc(100vw - ${theme.sidebar.width + theme.spacing(2)}px)`,
      height: `calc(100vh - ${theme.header.container.height}px)`,
      top: theme.header.container.height,
      left: theme.sidebar.width,
      position: 'absolute'
    }
  }))
  const classes = useStyles()
  const { center, zoom } = getHomePosition()
  return (
    <>
      <Box id={MAP_ID} className={classes.root} ref={containerRef}>
        <FocusControl Icon={CenterFocusStrong} center={[-90, 15]} zoom={6} />
        <FocusControl Icon={Home} center={center} zoom={zoom} />
        <BaseLayersControl
          width={64}
          height={50}
          gap={6}
          layers={baseLayers}
          onLayerSelected={setBaseLayerId}
        />
        {baseLayers &&
          baseLayers
            .filter(({ id }) => id === baseLayerId)
            ?.map(({ source, layer }, index) => (
              <Layer key={index} mapId={MAP_ID} source={source} layer={layer} />
            ))}
      </Box>
    </>
  )
}
